import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Users, QrCode, X, Clock, DollarSign, ShoppingCart, User, ChefHat, Wine, Flame, UtensilsCrossed, ArrowLeft, Receipt, CreditCard, Banknote } from 'lucide-react';
import { tableLabel } from '../../lib/tableLabel';

const statusColors = { available: 'bg-emerald-500/20 text-emerald-400', occupied: 'bg-red-500/25 text-red-400', reserved: 'bg-blue-accent/20 text-blue-accent' };
const itemStatusColors = { received: 'bg-blue-accent/20 text-blue-accent', preparing: 'bg-amber-500/20 text-amber-400', ready: 'bg-emerald-500/20 text-emerald-400', served: 'bg-zinc-500/20 text-zinc-400' };
const catIcons = { food: ChefHat, drinks: Wine, shisha: Flame };

function getElapsedMinutes(ts) { return Math.floor((Date.now() - new Date(ts).getTime()) / 60000); }

function TableDetail({ table, onClose }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDetails = useCallback(async () => {
    try {
      const { data } = await api.get(`/tables/${table.id}/details`);
      setDetails(data);
    } catch { toast.error('Failed to load table details'); }
    finally { setLoading(false); }
  }, [table.id]);

  useEffect(() => { fetchDetails(); const i = setInterval(fetchDetails, 8000); return () => clearInterval(i); }, [fetchDetails]);

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!details) return null;

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="back-to-tables">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className="text-3xl font-serif text-gold" data-testid="table-detail-number">Table {details.number}</h2>
            <Badge className={`${statusColors[details.status]} border-0 font-sans text-xs`}>{details.status}</Badge>
          </div>
          <div className="flex items-center gap-4 mt-1">
            <span className="text-zinc-500 text-xs font-sans flex items-center gap-1"><Users className="w-3 h-3" /> {details.capacity} seats</span>
            <span className="text-zinc-500 text-xs font-mono flex items-center gap-1"><QrCode className="w-3 h-3" /> {details.qr_code}</span>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white/[0.03] border border-white/5 rounded-sm p-3">
          <div className="flex items-center gap-2 mb-1">
            <User className="w-4 h-4 text-gold" />
            <span className="text-[11px] text-zinc-500 font-sans uppercase tracking-wider">People</span>
          </div>
          <p className="text-xl font-sans font-semibold text-white" data-testid="table-people-count">{details.total_people}</p>
        </div>
        <div className="bg-white/[0.03] border border-white/5 rounded-sm p-3">
          <div className="flex items-center gap-2 mb-1">
            <ShoppingCart className="w-4 h-4 text-blue-accent" />
            <span className="text-[11px] text-zinc-500 font-sans uppercase tracking-wider">Active Orders</span>
          </div>
          <p className="text-xl font-sans font-semibold text-white" data-testid="table-active-orders">{details.active_orders}</p>
        </div>
        <div className="bg-white/[0.03] border border-white/5 rounded-sm p-3">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-emerald-400" />
            <span className="text-[11px] text-zinc-500 font-sans uppercase tracking-wider">Revenue</span>
          </div>
          <p className="text-xl font-sans font-semibold text-gold" data-testid="table-revenue">${details.total_revenue.toFixed(2)}</p>
        </div>
        <div className="bg-white/[0.03] border border-white/5 rounded-sm p-3">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-amber-400" />
            <span className="text-[11px] text-zinc-500 font-sans uppercase tracking-wider">Total Orders</span>
          </div>
          <p className="text-xl font-sans font-semibold text-white">{details.orders?.length || 0}</p>
        </div>
      </div>

      {/* Customers */}
      {details.customers.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold mb-3">Currently Seated ({details.total_people})</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {details.customers.map(cust => (
              <div key={cust.id} className="flex items-center gap-3 p-3 bg-white/[0.03] border border-white/5 rounded-sm" data-testid={`table-customer-${cust.id}`}>
                <div className="w-9 h-9 rounded-full bg-gold/10 flex items-center justify-center text-xs font-sans text-gold font-bold">
                  {cust.name?.[0]?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-sans text-sm truncate">{cust.name}</p>
                  <p className="text-zinc-500 font-sans text-xs truncate">{cust.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-gold font-mono text-sm font-semibold">${cust.total_spend.toFixed(2)}</p>
                  <p className="text-zinc-600 text-xs font-sans">{cust.orders_count} orders</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Orders */}
      <div>
        <h3 className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold mb-3">Orders ({details.orders?.length || 0})</h3>
        {details.orders?.length === 0 ? (
          <p className="text-zinc-600 font-sans text-sm text-center py-8" data-testid="no-table-orders">No orders for this table</p>
        ) : (
          <div className="space-y-3">
            {details.orders.map(order => {
              const elapsed = getElapsedMinutes(order.created_at);
              return (
                <div key={order.id} className={`bg-white/[0.03] border rounded-sm p-4 ${order.order_status === 'active' ? 'border-white/10' : 'border-white/5 opacity-60'}`} data-testid={`table-order-${order.id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-sans text-sm font-medium">{order.customer_name}</span>
                        <span className="text-zinc-600 font-mono text-xs">#{order.id.slice(0, 8)}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge className={`${order.order_status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : order.order_status === 'voided' ? 'bg-red-500/20 text-red-400' : 'bg-zinc-500/20 text-zinc-400'} border-0 font-sans text-xs`}>
                          {order.order_status}
                        </Badge>
                        <Badge className={`${order.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' : order.payment_status === 'pending_cash' ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-500/20 text-zinc-400'} border-0 font-sans text-xs`}>
                          {order.payment_method} - {order.payment_status}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-gold font-sans font-semibold">${order.total.toFixed(2)}</p>
                      <span className="text-zinc-500 font-mono text-xs flex items-center gap-1 justify-end"><Clock className="w-3 h-3" /> {elapsed}m ago</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {order.items.map(item => {
                      const CatIcon = catIcons[item.category] || UtensilsCrossed;
                      return (
                        <div key={item.id} className="flex items-center justify-between py-1 border-t border-white/5">
                          <span className="text-zinc-300 font-sans text-sm flex items-center gap-1.5">
                            <CatIcon className="w-3 h-3 text-zinc-600" />
                            {item.quantity}x {item.name}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-zinc-500 font-mono text-xs">${item.total.toFixed(2)}</span>
                            <Badge className={`${itemStatusColors[item.status]} border-0 font-sans text-xs`}>{item.status}</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TablesPage() {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [newNum, setNewNum] = useState('');
  const [newCap, setNewCap] = useState('4');
  // Force-clear dialog state — kicks in when the backend returns a blocker
  // (active order / unpaid order / open tab) on Mark-Available. Manager can
  // optionally add an audit reason and confirm to void + close everything.
  const [forceClear, setForceClear] = useState(null); // { table, blockerMsg, reason, loading }
  // Quick "Settle & Clear" dialog state — opens when manager taps the
  // Settle button on an occupied/reserved table. Fetches the open tab
  // (if any) so cash/card can be picked in one tap, then marks the
  // table available immediately after settle succeeds.
  const [settle, setSettle] = useState(null); // { table, loading, preview, error, working }

  const fetchTables = useCallback(async () => {
    try { const { data } = await api.get('/tables'); setTables(data.sort((a, b) => a.number - b.number)); } catch (err) { console.error('[TablesPage.js] error:', err); }
  }, []);
  useEffect(() => {
    fetchTables();
    const i = setInterval(fetchTables, 5000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchTables(); };
    const onFocus = () => fetchTables();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(i);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchTables]);

  const setTableStatus = async (e, table, status) => {
    e.stopPropagation();
    try {
      await api.patch(`/tables/${table.id}/status`, { status });
      toast.success(`${tableLabel(table.number)} → ${status}`);
      fetchTables();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed';
      // If the guard blocked Mark-Available, surface a polished dialog with
      // a Force Clear button so the manager can clear the table immediately
      // (voids leftover orders + closes any open tab). Useful when the bill
      // was settled out-of-band but never marked paid in the app.
      if (status === 'available' && err.response?.status === 400) {
        setForceClear({
          table,
          blockerMsg: detail,
          reason: 'Bill settled at bar',
          loading: false,
        });
        return;
      }
      // Surface the full guard-rail message for ≥6s so the floor manager sees why.
      toast.error(detail, { duration: 6000 });
    }
  };

  const confirmForceClear = async () => {
    if (!forceClear?.table) return;
    setForceClear(prev => prev ? { ...prev, loading: true } : prev);
    try {
      const { data } = await api.patch(`/tables/${forceClear.table.id}/status`, {
        status: 'available',
        force: true,
        force_reason: (forceClear.reason || '').trim(),
      });
      const acts = (data?.forced_actions || []).map(a => `${a.action.replace(/_/g, ' ')} (${a.count})`).join(', ');
      toast.success(`${tableLabel(forceClear.table.number)} cleared · ${acts || 'no-op'}`, { duration: 6000 });
      setForceClear(null);
      fetchTables();
    } catch (err2) {
      toast.error(err2.response?.data?.detail || 'Force-clear failed', { duration: 6000 });
      setForceClear(prev => prev ? { ...prev, loading: false } : prev);
    }
  };

  // Open the Settle & Clear flow: find the open tab on this table (if any),
  // fetch the settlement preview, and render cash/card buttons. If there's
  // no tab, we still show the dialog so the manager can confirm collecting
  // the bill out-of-band and clearing the table in one step.
  const openSettleAndClear = async (e, table) => {
    e.stopPropagation();
    setSettle({ table, loading: true, preview: null, error: null, working: false });
    try {
      // Look for a tab order on this table — manager tab orders carry
      // payment_method=tab and payment_status=tab while the tab is open.
      const { data: orders } = await api.get(`/orders?table_id=${table.id}`);
      const tabOrder = (orders || []).find(
        (o) => o.payment_method === 'tab' && o.payment_status === 'tab' &&
               !['cancelled', 'voided'].includes(o.order_status)
      );
      if (!tabOrder) {
        setSettle({ table, loading: false, preview: null, error: null, working: false });
        return;
      }
      const { data: preview } = await api.get(`/tabs/by-order/${tabOrder.id}`);
      setSettle({
        table, loading: false,
        preview: { ...preview, order_id: tabOrder.id },
        error: null, working: false,
      });
    } catch (err) {
      setSettle({ table, loading: false, preview: null, error: err.response?.data?.detail || 'Failed to load tab', working: false });
    }
  };

  const confirmSettleAndClear = async (method) => {
    if (!settle?.table) return;
    setSettle(prev => prev ? { ...prev, working: true } : prev);
    try {
      // Step 1: settle the tab (if there is one) so all sibling orders flip to paid.
      if (settle.preview?.order_id) {
        const url = method
          ? `/tabs/by-order/${settle.preview.order_id}/settle?settlement_method=${method}`
          : `/tabs/by-order/${settle.preview.order_id}/settle`;
        await api.post(url);
      }
      // Step 2: force-flip the table to available. Force=true so any
      // stragglers (e.g. a separate unpaid cash order on the same table)
      // are also voided as part of the close — same audit trail as the
      // dedicated Force Clear flow.
      const reason = settle.preview?.order_id
        ? `Tab settled (${method || 'card'}) — table cleared`
        : 'Bill settled at bar';
      const { data: clearData } = await api.patch(`/tables/${settle.table.id}/status`, {
        status: 'available',
        force: true,
        force_reason: reason,
      });
      const acts = (clearData?.forced_actions || []).map(a => `${a.action.replace(/_/g, ' ')} (${a.count})`).join(', ');
      toast.success(
        `${tableLabel(settle.table.number)} settled & cleared${acts ? ` · ${acts}` : ''}`,
        { duration: 6000 }
      );
      setSettle(null);
      fetchTables();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Settle & clear failed', { duration: 6000 });
      setSettle(prev => prev ? { ...prev, working: false } : prev);
    }
  };

  const addTable = async () => {
    if (!newNum) return;
    try {
      await api.post('/tables', { number: parseInt(newNum), capacity: parseInt(newCap) });
      toast.success('Table added');
      setNewNum(''); setNewCap('4'); fetchTables();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (selectedTable) {
    return <TableDetail table={selectedTable} onClose={() => setSelectedTable(null)} />;
  }

  const occupiedCount = tables.filter(t => t.status === 'occupied').length;
  const totalSeats = tables.reduce((s, t) => s + (t.capacity || 0), 0);

  return (
    <div className="animate-fadeIn">
      {/* Summary */}
      <div className="flex items-center gap-4 mb-4">
        <span className="text-sm font-sans text-zinc-400">{tables.length} tables</span>
        <span className="text-sm font-sans text-emerald-400 font-semibold">{tables.length - occupiedCount} available</span>
        <span className="text-sm font-sans text-red-400 font-semibold">{occupiedCount} occupied</span>
        <span className="text-sm font-sans text-zinc-500">{totalSeats} total seats</span>
      </div>

      {/* Add Table */}
      <div className="flex gap-2 mb-6">
        <Input type="number" placeholder="Table #" value={newNum} onChange={e => setNewNum(e.target.value)} className="w-28 bg-transparent border border-white/10 text-white font-sans" data-testid="new-table-number-input" />
        <Input type="number" placeholder="Capacity" value={newCap} onChange={e => setNewCap(e.target.value)} className="w-28 bg-transparent border border-white/10 text-white font-sans" data-testid="new-table-capacity-input" />
        <Button onClick={addTable} className="bg-gold text-black rounded-sm font-sans text-xs font-bold" data-testid="add-table-button"><Plus className="w-3 h-3 mr-1" /> Add Table</Button>
      </div>

      {/* Table Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
        {tables.map(t => (
          <div
            key={t.id}
            onClick={() => setSelectedTable(t)}
            className={`bg-white/[0.03] border rounded-sm p-5 text-center card-hover cursor-pointer transition-all ${t.status === 'occupied' ? 'border-red-500/40 hover:border-red-400' : t.status === 'reserved' ? 'border-blue-accent/30 hover:border-blue-accent' : 'border-emerald-500/30 hover:border-emerald-400'}`}
            data-testid={`table-card-${t.id}`}
          >
            <p className="text-gold font-serif text-3xl font-semibold mb-2">{tableLabel(t.number)}</p>
            <Badge className={`${statusColors[t.status]} border-0 font-sans text-xs font-bold uppercase tracking-[0.15em] mb-3`}>{t.status}</Badge>
            <div className="flex items-center justify-center gap-1 text-xs text-zinc-500 font-sans mb-1">
              <Users className="w-3 h-3" /> {t.capacity} seats
            </div>
            <div className="flex items-center justify-center gap-1 text-xs text-zinc-600 font-mono">
              <QrCode className="w-3 h-3" /> {t.qr_code}
            </div>
            {/* Manual status toggle + Quick Settle — manager can settle
                the bill (cash/card) AND mark the table available in one
                tap, or just flip status manually if no payment is pending. */}
            <div className="flex flex-col items-center gap-1 mt-3" onClick={e => e.stopPropagation()}>
              {t.status !== 'available' ? (
                <>
                  <button
                    onClick={(e) => openSettleAndClear(e, t)}
                    className="w-full px-2 py-1 bg-gold/15 text-gold hover:bg-gold/25 border border-gold/40 rounded-sm font-sans text-[10px] uppercase tracking-[0.15em] font-semibold"
                    data-testid={`settle-and-clear-${t.id}`}
                  >Settle &amp; Clear</button>
                  <button
                    onClick={(e) => setTableStatus(e, t, 'available')}
                    className="w-full px-2 py-1 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20 rounded-sm font-sans text-[10px] uppercase tracking-[0.15em]"
                    data-testid={`mark-available-${t.id}`}
                  >Mark Available</button>
                </>
              ) : (
                <button
                  onClick={(e) => setTableStatus(e, t, 'occupied')}
                  className="w-full px-2 py-1 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20 rounded-sm font-sans text-[10px] uppercase tracking-[0.15em]"
                  data-testid={`mark-occupied-${t.id}`}
                >Mark Occupied</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Force-clear dialog — replaces window.confirm/prompt for a clean,
          manager-friendly flow when a table still has active orders /
          unpaid bills / an open tab. */}
      <Dialog open={!!forceClear} onOpenChange={(v) => { if (!v && !forceClear?.loading) setForceClear(null); }}>
        <DialogContent className="bg-bg-primary border border-amber-500/40 max-w-md" data-testid="force-clear-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-white text-lg">
              Force clear {tableLabel(forceClear?.table?.number)}?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div className="rounded-sm border border-amber-500/30 bg-amber-500/5 px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-[0.18em] text-amber-400 font-sans font-bold mb-1">Blocker</p>
              <p className="text-xs text-amber-100 font-sans leading-relaxed" data-testid="force-clear-blocker-msg">
                {forceClear?.blockerMsg}
              </p>
            </div>
            <p className="text-xs text-zinc-400 font-sans leading-relaxed">
              If you&apos;ve already collected the payment out-of-band (cash drawer, EFTPOS slip), force-clearing will{' '}
              <strong className="text-red-300">void leftover orders</strong> and{' '}
              <strong className="text-red-300">close any open tab</strong> so the table can be re-seated. This is audit-logged.
            </p>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans font-bold">
                Reason (audit log)
              </label>
              <Input
                value={forceClear?.reason || ''}
                onChange={(e) => setForceClear(prev => prev ? { ...prev, reason: e.target.value } : prev)}
                placeholder="e.g. Bill settled at bar"
                className="bg-transparent border-white/10 focus:border-gold text-white font-sans text-sm h-10"
                data-testid="force-clear-reason"
                disabled={forceClear?.loading}
              />
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                onClick={() => setForceClear(null)}
                disabled={forceClear?.loading}
                className="flex-1 bg-transparent border border-white/15 text-zinc-300 hover:bg-white/5 rounded-sm font-sans h-10"
                data-testid="force-clear-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmForceClear}
                disabled={forceClear?.loading}
                className="flex-1 bg-red-500/15 border border-red-500/50 text-red-200 hover:bg-red-500/25 rounded-sm font-sans font-bold uppercase tracking-[0.08em] text-xs h-10 disabled:opacity-50"
                data-testid="force-clear-confirm"
              >
                {forceClear?.loading ? 'Clearing…' : 'Force Clear Table'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settle & Clear dialog — manager picks Cash/Card to settle the
          open tab (if any) and immediately mark the table available.
          Falls back to a "no open tab — clear anyway?" path so the
          button is useful even on tables with paid direct orders. */}
      <Dialog open={!!settle} onOpenChange={(v) => { if (!v && !settle?.working) setSettle(null); }}>
        <DialogContent className="bg-bg-primary border border-gold/40 max-w-md" data-testid="settle-and-clear-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-white text-lg flex items-center gap-2">
              <Receipt className="w-5 h-5 text-gold" />
              Settle &amp; Clear {tableLabel(settle?.table?.number)}
            </DialogTitle>
          </DialogHeader>
          {settle?.loading && (
            <div className="flex items-center justify-center py-8" data-testid="settle-loading">
              <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {!settle?.loading && settle?.error && (
            <p className="text-xs text-red-300 font-sans bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2" data-testid="settle-error">
              {settle.error}
            </p>
          )}
          {!settle?.loading && !settle?.error && settle?.preview && (() => {
            const p = settle.preview;
            const cashEligible = !!p.discount_eligible_for_cash;
            return (
              <div className="space-y-4 pt-1" data-testid="settle-preview">
                <div className="rounded-sm border border-white/10 bg-white/[0.02] px-3 py-2.5 space-y-1.5 font-sans text-sm">
                  <div className="flex justify-between text-zinc-300">
                    <span>Bill ({(p.orders || []).length} order{(p.orders || []).length === 1 ? '' : 's'})</span>
                    <span className="font-mono">${Number(p.sum_total).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-zinc-400 text-xs">
                    <span>Less upfront deposit</span>
                    <span className="text-emerald-400 font-mono">−${Number(p.upfront_amount).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-white/10">
                    <span className="text-white font-medium">Owed at bar</span>
                    <span className="text-gold font-bold font-mono text-lg" data-testid="settle-and-clear-owed">${Number(p.owed_at_counter).toFixed(2)}</span>
                  </div>
                  {cashEligible && (
                    <div className="pt-2 border-t border-emerald-500/30 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-emerald-300 font-semibold uppercase tracking-[0.08em]">If Cash · 10% Off</span>
                        <span className="text-emerald-300 font-mono">−${Number(p.cash_discount_amount).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white font-medium">Cash owed</span>
                        <span className="text-emerald-300 font-bold font-mono" data-testid="settle-and-clear-cash-owed">${Number(p.cash_owed_at_counter).toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-zinc-500 font-sans leading-relaxed">
                  Settling closes the tab, marks every order paid, and immediately flips {tableLabel(settle.table.number)} to Available.
                </p>
                {cashEligible ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={() => confirmSettleAndClear('cash')}
                      disabled={settle.working}
                      className="h-11 bg-emerald-500/20 border border-emerald-500/60 text-emerald-200 hover:bg-emerald-500/30 font-bold uppercase tracking-[0.05em] text-xs rounded-sm disabled:opacity-50"
                      data-testid="settle-and-clear-cash"
                    >
                      <Banknote className="w-4 h-4 mr-1.5" />
                      {settle.working ? 'Closing…' : `Cash · $${Number(p.cash_owed_at_counter).toFixed(2)}`}
                    </Button>
                    <Button
                      onClick={() => confirmSettleAndClear('card')}
                      disabled={settle.working}
                      className="h-11 bg-blue-accent/15 border border-blue-accent/50 text-blue-accent hover:bg-blue-accent/25 font-bold uppercase tracking-[0.05em] text-xs rounded-sm disabled:opacity-50"
                      data-testid="settle-and-clear-card"
                    >
                      <CreditCard className="w-4 h-4 mr-1.5" />
                      {settle.working ? 'Closing…' : `Card · $${Number(p.owed_at_counter).toFixed(2)}`}
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={() => confirmSettleAndClear('cash')}
                      disabled={settle.working}
                      className="h-11 bg-emerald-500/15 border border-emerald-500/50 text-emerald-200 hover:bg-emerald-500/25 font-bold uppercase tracking-[0.05em] text-xs rounded-sm disabled:opacity-50"
                      data-testid="settle-and-clear-cash"
                    >
                      <Banknote className="w-4 h-4 mr-1.5" />
                      {settle.working ? 'Closing…' : `Cash · $${Number(p.owed_at_counter).toFixed(2)}`}
                    </Button>
                    <Button
                      onClick={() => confirmSettleAndClear('card')}
                      disabled={settle.working}
                      className="h-11 bg-blue-accent/15 border border-blue-accent/50 text-blue-accent hover:bg-blue-accent/25 font-bold uppercase tracking-[0.05em] text-xs rounded-sm disabled:opacity-50"
                      data-testid="settle-and-clear-card"
                    >
                      <CreditCard className="w-4 h-4 mr-1.5" />
                      {settle.working ? 'Closing…' : `Card · $${Number(p.owed_at_counter).toFixed(2)}`}
                    </Button>
                  </div>
                )}
                <Button
                  onClick={() => setSettle(null)}
                  disabled={settle.working}
                  className="w-full bg-transparent border border-white/15 text-zinc-400 hover:bg-white/5 rounded-sm font-sans h-9 text-xs"
                  data-testid="settle-and-clear-cancel"
                >
                  Cancel
                </Button>
              </div>
            );
          })()}
          {!settle?.loading && !settle?.error && !settle?.preview && settle?.table && (
            <div className="space-y-3 pt-1" data-testid="settle-no-tab">
              <p className="text-sm text-zinc-300 font-sans">
                {tableLabel(settle.table.number)} has no open tab. If you&apos;ve already collected the bill at the bar, you can mark this table available now.
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => setSettle(null)}
                  disabled={settle.working}
                  className="flex-1 bg-transparent border border-white/15 text-zinc-300 hover:bg-white/5 rounded-sm font-sans h-10"
                  data-testid="settle-and-clear-cancel-empty"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => confirmSettleAndClear(null)}
                  disabled={settle.working}
                  className="flex-1 bg-emerald-500/20 border border-emerald-500/60 text-emerald-200 hover:bg-emerald-500/30 rounded-sm font-bold uppercase tracking-[0.08em] text-xs h-10 disabled:opacity-50"
                  data-testid="settle-and-clear-confirm-empty"
                >
                  {settle.working ? 'Clearing…' : 'Clear Table'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
